import React, { useEffect, useState } from 'react';
import { History, X } from 'lucide-react';
import { capacityLimitsService, CapacityHistoryEntry, LimitScope } from 'api/services/capacityLimits.service';
import { describeChange, directionTone } from '../capacityEnforcementRules';

/** Timeline of one scope's capacity changes: date, who, old → new, why. */
const CapacityHistoryModal: React.FC<{ scope: LimitScope; value: string; title: string; onClose: () => void }> =
  ({ scope, value, title, onClose }) => {
    const [rows, setRows] = useState<CapacityHistoryEntry[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      let live = true;
      capacityLimitsService.history(scope, value).then(res => {
        if (!live) return;
        if (res.success) setRows(res.data || []); else setError(res.error || 'Failed to load history');
      });
      return () => { live = false; };
    }, [scope, value]);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <History className="w-5 h-5" /> Capacity history — {title}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X className="w-5 h-5" /></button>
          </div>
          <div className="px-6 py-4 overflow-y-auto">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
            {!rows && !error && <p className="text-sm text-gray-500">Loading…</p>}
            {rows && rows.length === 0 && <p className="text-sm text-gray-500">No changes recorded yet.</p>}
            {rows && rows.length > 0 && (
              <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2 space-y-4">
                {rows.map(e => (
                  <li key={e.id} className="ml-4">
                    <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" />
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <time className="text-gray-500">{new Date(e.changed_at).toLocaleString()}</time>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${directionTone(e.direction)}`}>{e.direction}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{describeChange(e)}</span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">“{e.reason}”</p>
                    <p className="text-xs text-gray-500">by {e.changed_by} · via {e.change_path.toLowerCase().replace(/_/g, ' ')}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    );
  };

export default CapacityHistoryModal;
